import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api/client';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: { id: number; email: string } | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<{ id: number; email: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar token existente
    const token = localStorage.getItem('crm_token');
    const savedUser = localStorage.getItem('crm_user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));

        // Validar contra el servidor que el token sigue siendo válido.
        // Si el servidor lo rechaza (expirado o secreto rotado), se cierra
        // la sesión en lugar de dejar la app en un estado "fantasma".
        api.dashboard.summary().catch((err) => {
          const message = err instanceof Error ? err.message : '';
          if (
            message.includes('Token inválido') ||
            message.includes('No autorizado') ||
            message.includes('Configuración del servidor')
          ) {
            localStorage.removeItem('crm_token');
            localStorage.removeItem('crm_user');
            setUser(null);
          }
        });
      } catch {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.auth.login(email, password);
    localStorage.setItem('crm_token', response.token);
    localStorage.setItem('crm_user', JSON.stringify(response.user));
    setUser(response.user);
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated: !!user, loading, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
